import {
  DeleteOutlined,
  LoadingOutlined,
  EditOutlined,
  TeamOutlined,
  CalendarOutlined,
  UserOutlined,
  FileOutlined,
} from '@ant-design/icons'
import {
  Card,
  Typography,
  Space,
  Tooltip,
  Popconfirm,
  Avatar,
  Button,
  Flex,
} from 'antd'
import { format } from 'date-fns'
import React from 'react'

import { useAssociatedEquipments } from '~/hooks/Equipments/AssociatedEquipments'
import { useEquipments } from '~/hooks/Equipments/EquipmentsHooks'
import { translate } from '~/utils/locale'
import CreateAssociatedEquipments from '../AssociatedEquipments/Create'

import { LoadingIndicator } from './styles'
import { EditButton } from '~/components/DefaultLayout/Buttons/buttonStyle'
import { createLetterAvatar } from '~/utils/letter'

const EquipmentsDetails: React.FC = () => {
  const { isLoading, equipments, setIsEdit } = useEquipments()
  const { deleteAssociatedEquipment } = useAssociatedEquipments()
  const { Text, Title } = Typography

  if (isLoading || !equipments) {
    return (
      <LoadingIndicator>
        <LoadingOutlined style={{ fontSize: 64, margin: 'auto' }} spin />
      </LoadingIndicator>
    )
  }

  const formatterDate = (value: Date) => {
    return format(
      value,
      `dd/MM/yyyy '${translate('EquipmentsTabsDetails.dateString')}' HH:mm'h'`,
    )
  }
  const newDate = formatterDate(new Date(equipments.expiration_date))

  // function deleteImage() {
  //   localStorage.setItem('removeImage', 'delete')
  //   window.location.reload()
  // }

  const isRemoveImage = localStorage.getItem('removeImage')

  return (
    <>
      <Card
        bordered={false}
        title={
          <Title level={5} style={{ marginBottom: 0, fontSize: 18 }}>
            {translate('EquipmentsTabsDetails.cardTitle')}
          </Title>
        }
        extra={
          <EditButton icon={<EditOutlined />} onClick={() => setIsEdit(true)}>
            {translate('EquipmentsTabsDetails.editButton')}
          </EditButton>
        }
        style={{
          display: 'inline-block',
          width: '100%',
        }}
      >
        <Flex gap={20}>
          {equipments.photo_url !== null && isRemoveImage === 'image' ? (
            <Avatar src={equipments.photo_url} shape="square" size={120} />
          ) : (
            <Avatar
              shape="square"
              size={120}
              style={{
                fontSize: 30,
                color: '#fff',
                background: 'linear-gradient(135deg, #3C8ACB 0%, #3969B7 100%)',
                borderRadius: 12,
              }}
            >
              {createLetterAvatar(`${equipments.name}`)}
            </Avatar>
          )}

          <Flex vertical gap={10} align="stretch">
            <Flex gap={60}>
              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <TeamOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('EquipmentsTabsDetails.equipmentName')}
                  </Title>
                  <Text>{equipments.name}</Text>
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <CalendarOutlined
                      style={{ fontSize: 12, color: '#464545' }}
                    />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Space>
                    <Title level={5} style={{ marginBottom: 0 }}>
                      {translate('EquipmentsTabsDetails.associatedEquipment')}
                    </Title>
                    <CreateAssociatedEquipments />
                  </Space>

                  {equipments.associatedEquipment !== null ? (
                    <div className="delete_association">
                      <Space>
                        <Text>
                          {equipments.associatedEquipment.equipment.name}
                        </Text>
                        <Tooltip
                          title={translate(
                            'EquipmentsTabsDetails.deleteTooltip',
                          )}
                        >
                          <Popconfirm
                            title={translate(
                              'EquipmentsTabsDetails.deletePopconfirm',
                            )}
                            onConfirm={() =>
                              deleteAssociatedEquipment(
                                equipments.associatedEquipment.equipment.id,
                                equipments.associatedEquipment.id,
                              )
                            }
                          >
                            <Button
                              ghost
                              icon={<DeleteOutlined />}
                              danger
                              size="small"
                            />
                          </Popconfirm>
                        </Tooltip>
                      </Space>
                    </div>
                  ) : (
                    <Text>
                      {translate('EquipmentsTabsDetails.noAssociatedEquipment')}
                    </Text>
                  )}
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <TeamOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('EquipmentsTabsDetails.equipmentLocale')}
                  </Title>
                  <Text>{translate('EquipmentsTabsDetails.inventory')}</Text>
                </div>
              </Flex>
            </Flex>

            <Flex gap={60}>
              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <UserOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('EquipmentsTabsDetails.identifier')}
                  </Title>
                  {equipments.identifier ? (
                    <Text>{equipments.identifier}</Text>
                  ) : (
                    'Nenhuma informação disponível.'
                  )}
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <FileOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('EquipmentsTabsDetails.expirationDate')}
                  </Title>
                  {newDate ? (
                    <Text>{newDate}</Text>
                  ) : (
                    'Nenhuma informação disponível.'
                  )}
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <TeamOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('EquipmentsTabsDetails.category')}
                  </Title>
                  {equipments.category.title ? (
                    <Text>{equipments.category.title}</Text>
                  ) : (
                    'Nenhuma informação disponível.'
                  )}
                </div>
              </Flex>
            </Flex>

            <Flex gap={60}>
              <Flex gap={10} style={{ minWidth: 350 }}>
                <Avatar
                  shape="square"
                  icon={
                    <UserOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('EquipmentsTabsDetails.description')}
                  </Title>
                  {equipments.description ? (
                    <Text>{equipments.description}</Text>
                  ) : (
                    'Nenhuma informação disponível.'
                  )}
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </>
  )
}

export default EquipmentsDetails
